import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Grid, List, Package } from 'lucide-react';
import ProductCard from './ProductCard';
import { useProducts } from '../hooks/useApi';

interface ProductShowcaseProps {
  showHeader?: boolean;
  backgroundClass?: string;
}

const ProductShowcase: React.FC<ProductShowcaseProps> = ({ showHeader = true, backgroundClass = 'bg-gray-50' }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // 从静态 JSON 获取产品数据
  const { data: products, loading, error } = useProducts({
    sort: 'sortOrder',
    limit: 50
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // 加载状态
  if (loading) {
    return (
      <section className={`section-padding ${backgroundClass}`}>
        <div className="container-max">
          {showHeader && (
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                产品展示
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                提供多种工业炉及燃烧控制设备，满足客户不同产品、不同工况需求
              </p>
            </div>
          )}
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span className="ml-4 text-gray-600">加载中...</span>
          </div>
        </div>
      </section>
    );
  }

  // 错误状态
  if (error) {
    return (
      <section className={`section-padding ${backgroundClass}`}>
        <div className="container-max">
          {showHeader && (
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                产品展示
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                提供多种工业炉及燃烧控制设备，满足客户不同产品、不同工况需求
              </p>
            </div>
          )}
          <div className="text-center py-20">
            <div className="text-red-500 mb-4">
              <Package className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              加载失败
            </h3>
            <p className="text-gray-500 mb-4">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              重新加载
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`section-padding ${backgroundClass}`}>
      <div className="container-max">
        {/* Section Header */}
        {showHeader && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              产品展示
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              提供多种工业炉及燃烧控制设备，满足客户不同产品、不同工况需求
            </p>
          </motion.div>
        )}

        {/* View Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-end items-center mb-8"
        >
          {/* View Mode Toggle */}
          <div className="flex items-center space-x-2 bg-white rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'grid'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className={`grid gap-6 ${
            viewMode === 'grid'
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              : 'grid-cols-1'
          }`}
        >
          {products.map((product) => (
            <motion.div key={product.id} variants={itemVariants}>
              <ProductCard
                product={product}
                className={viewMode === 'list' ? 'flex flex-row' : ''}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Empty State */}
        {products.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-400 mb-4">
              <Package className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              暂无产品
            </h3>
            <p className="text-gray-500">
              请联系我们的销售团队了解更多产品信息
            </p>
          </motion.div>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              需要定制化解决方案？
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              我们的专业团队可以根据您的具体需求，提供定制化的陶瓷生产设备解决方案
            </p>
            <div className="flex justify-center">
              <a
                href="/contact"
                className="btn-primary"
              >
                联系我们
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductShowcase;
